import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type SupplierPayableStatus = "PENDIENTE" | "PARCIAL" | "PAGADA" | "ANULADA";
export type SupplierPaymentMethod = "EFECTIVO" | "TRANSFERENCIA" | "CHEQUE" | "TARJETA" | "NEQUI" | "DAVIPLATA" | "OTRO";

export const SUPPLIER_PAYABLE_STATUS_LABEL: Record<SupplierPayableStatus, string> = {
  PENDIENTE: "Pendiente",
  PARCIAL: "Abonada parcialmente",
  PAGADA: "Pagada",
  ANULADA: "Anulada",
};

export const SUPPLIER_PAYMENT_METHOD_LABEL: Record<SupplierPaymentMethod, string> = {
  EFECTIVO: "Efectivo",
  TRANSFERENCIA: "Transferencia",
  CHEQUE: "Cheque",
  TARJETA: "Tarjeta",
  NEQUI: "Nequi",
  DAVIPLATA: "Daviplata",
  OTRO: "Otro",
};

export interface SupplierPaymentRecord {
  id: string;
  amount: string;
  paymentMethod: SupplierPaymentMethod;
  paymentDate: string;
  notes: string | null;
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
}

export interface SupplierPayableRecord {
  id: string;
  purchaseId: string;
  supplierId: string;
  branchId: string;
  principal: string;
  balance: string;
  status: SupplierPayableStatus;
  notes: string | null;
  dueDate: string | null;
  createdAt: string;
  purchase?: { id: string; purchaseDate: string; invoiceNumber: string | null; total: string; status: string };
  supplier?: { id: string; name: string; taxId: string | null };
  branch?: { id: string; name: string };
  payments: SupplierPaymentRecord[];
}

export interface ListSupplierPayablesFilters {
  page?: number;
  pageSize?: number;
  supplierId?: string;
  status?: SupplierPayableStatus;
}

/** Cuentas por Pagar a Proveedores — ver supplier-payable.service.ts en el backend. Nace de una compra al recibirla. */
export function useSupplierPayables(filters: ListSupplierPayablesFilters = {}) {
  return useQuery({
    queryKey: ["store", "supplier-payables", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<SupplierPayableRecord[]>>("/supplier-payables", { params: filters });
      return data;
    },
  });
}

export function useSupplierPayable(id: string | null) {
  return useQuery({
    queryKey: ["store", "supplier-payables", "detail", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<SupplierPayableRecord>>(`/supplier-payables/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useRegisterSupplierPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      amount,
      paymentMethod,
      paymentDate,
      notes,
    }: {
      id: string;
      amount: number | string;
      paymentMethod?: SupplierPaymentMethod;
      paymentDate?: string;
      notes?: string;
    }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<SupplierPayableRecord>>(`/supplier-payables/${id}/payments`, {
        amount,
        paymentMethod,
        paymentDate,
        notes,
      });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "supplier-payables"] }),
  });
}

export function useUpdateSupplierPayableDueDate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dueDate }: { id: string; dueDate: string | null }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<SupplierPayableRecord>>(`/supplier-payables/${id}/due-date`, { dueDate });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "supplier-payables"] }),
  });
}

export interface SupplierPayableSummaryRow {
  supplierId: string;
  supplierName: string;
  pendingCount: number;
  pendingBalance: number;
}

/** Deuda pendiente agrupada por proveedor. */
export function useSupplierPayableSummary() {
  return useQuery({
    queryKey: ["store", "supplier-payables", "supplier-summary"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<SupplierPayableSummaryRow[]>>("/supplier-payables/supplier-summary");
      return data.data;
    },
  });
}
